import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShieldCheck, Scale, Users, ArrowRight, PlayCircle, Quote, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { QualiopiTeaser } from '../components/QualiopiTeaser';
import { ContactCTA } from '../components/ContactCTA';
import { useSeo } from '../hooks/useSeo';
import { CFA_INFO, PHONE_DISPLAY, PHONE_HREF } from '../data/constants';
const TEAM_VIDEOS = [{
  name: CFA_INFO.president,
  role: 'Fondateur & Directeur',
  quote: 'Pourquoi nous avons créé Zillenium.',
  url: 'https://www.instagram.com/reel/DVOBbA0DIMk/embed',
  accent: 'bg-brand-orange',
  offset: ''
}, {
  name: 'Justine Delille',
  role: 'Responsable pédagogique',
  quote: 'Comment nous accompagnons chaque apprenant.',
  url: 'https://www.instagram.com/reel/DVQg1eADa3_/embed',
  accent: 'bg-primary',
  offset: 'lg:mt-16'
}];
const VALUES = [{
  icon: Heart,
  title: 'Bienveillance',
  text: 'Un cadre humain et exigeant, où personne n\'est laissé de côté.',
  iconBg: 'bg-lavande',
  iconColor: 'text-primary',
  bar: 'bg-primary'
}, {
  icon: ShieldCheck,
  title: 'Confiance',
  title2: '',
  text: 'Valoriser chaque réussite, même la plus petite, pour construire l\'assurance.',
  iconBg: 'bg-brand-orange/10',
  iconColor: 'text-brand-orange',
  bar: 'bg-brand-orange'
}, {
  icon: Scale,
  title: 'Responsabilité',
  text: 'Devenir autonome, fiable et acteur de son propre parcours.',
  iconBg: 'bg-primary/10',
  iconColor: 'text-primary',
  bar: 'bg-primary-dark'
}, {
  icon: Users,
  title: 'Collectif',
  text: 'L\'esprit d\'équipe avant tout : apprenants, entreprises et équipe pédagogique.',
  iconBg: 'bg-lavande',
  iconColor: 'text-primary',
  bar: 'bg-brand-orange'
}];
function VideoFrame({
  video
}: {
  video: (typeof TEAM_VIDEOS)[number];
}) {
  return <div className={`w-full ${video.offset}`} data-id="element-1092" data-mp="2hdgw6g6wyxsw">
      <div className="bg-white p-3 rounded-[2rem] shadow-2xl" data-id="element-1093" data-mp="2hcnye4pxnimw">
        <div className="relative rounded-[1.5rem] overflow-hidden bg-slate-100 aspect-[9/16]" data-id="element-1094" data-mp="2w2d4mecvucu1">
          <iframe src={video.url} title={`${video.name} — ${video.role}`} className="absolute inset-0 w-full h-full" frameBorder="0" scrolling="no" allowTransparency={true} data-id="element-1095" data-mp="2r2h2kknqwvh4"></iframe>
        </div>
        <div className="px-3 pt-5 pb-2" data-id="element-1096" data-mp="2dblzjozxh8lg">
          <span className={`block h-1 w-10 rounded-full ${video.accent} mb-4`} data-id="element-1097" data-mp="2dcdjg2k5xhvw" />
          <p className="font-extrabold font-heading text-lg text-primary-dark leading-tight" data-id="element-1098" data-mp="28bpvxqv57j6f">
            {video.name}
          </p>
          <p className="text-sm font-semibold text-brand-orange mb-2" data-id="element-1099" data-mp="3osveby843cgf">
            {video.role}
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-1100" data-mp="3ju0r8k3431qi">
            {video.quote}
          </p>
        </div>
      </div>
    </div>;
}
export function CFA() {
  useSeo({
    title: "Notre CFA à Joigny — L'esprit Zillenium",
    description: "Découvrez Zillenium Alternance, CFA certifié Qualiopi à Joigny (89). Notre équipe, nos valeurs et notre approche de l'alternance en vidéo : un accompagnement humain du premier contact au diplôme.",
    path: '/le-cfa'
  });
  return <div className="flex flex-col w-full" data-id="element-1101" data-mp="ivdtbc8b7rsq">
      {/* Hero éditorial : vidéos en vedette */}
      <section className="bg-primary-dark text-white relative overflow-hidden" data-id="element-1102" data-mp="ix82jta7xvgz">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-primary rounded-full blur-[130px] opacity-40 -translate-y-1/3 translate-x-1/4 pointer-events-none" data-id="element-1103" data-mp="53qm4hctwyv6" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-orange rounded-full blur-[130px] opacity-20 translate-y-1/3 pointer-events-none" data-id="element-1104" data-mp="wu3o2l7npfdx" />

        <div className="container max-w-6xl mx-auto relative z-10 py-20 lg:py-28" data-id="element-1105" data-mp="3a2o6plgcwu0u">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center" data-id="element-1106" data-mp="3a0l8ie51vul1">
            <div data-id="element-1107" data-mp="352jnvorz7n7g">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-6" data-id="element-1108" data-mp="3567henxf89m6">
                L'esprit Zillenium
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-8" data-id="element-1109" data-mp="2w4ra4p1wknm6">
                L'alternance
                <span className="block text-brand-orange" data-id="element-1110" data-mp="3ntue03pvnvya">autrement.</span>
              </h1>
              <p className="text-lavande text-lg md:text-xl leading-relaxed mb-6" data-id="element-1111" data-mp="3j3472m1szl1h">
                Chez Zillenium, nous ne formons pas de simples étudiants, mais
                les professionnels de demain. Notre pédagogie repose sur
                l'action, la confiance et un accompagnement sur-mesure.
              </p>
              <p className="text-white font-bold text-lg mb-10" data-id="element-1112" data-mp="amrh7evw0ti0">
                Le mieux, c'est de nous entendre le dire. Écoutez notre équipe →
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1113" data-mp="1y9dq552vddho">
                <Button size="lg" className="bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-7 shadow-xl group" asChild data-id="element-1114" data-mp="1y9x6wigs9vu4">
                  <Link to="/candidater" className="flex items-center" data-id="element-1115" data-mp="2788xwllkavn3">
                    Rejoindre Zillenium
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1116" data-mp="2h8jbw8ff873b" />
                  </Link>
                </Button>
                <a href={PHONE_HREF} className="flex items-center justify-center gap-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-xl px-8 py-4 transition-colors" data-id="element-1117" data-mp="2m0j41ep91vpc">
                  <Phone className="w-5 h-5 text-brand-orange shrink-0" data-id="element-1118" data-mp="2zyr9kb0cq5c4" />
                  <span className="leading-tight text-left" data-id="element-1119" data-mp="2zwwpt00tgj9m">
                    <span className="block text-xs uppercase tracking-wider text-lavande" data-id="element-1120" data-mp="zxjbeu29atby">
                      Appelez-nous
                    </span>
                    <span className="text-lg" data-id="element-1121" data-mp="19mkrek8sxapx">{PHONE_DISPLAY}</span>
                  </span>
                </a>
              </div>
            </div>

            {/* Les deux vidéos, décalées */}
            <div className="grid grid-cols-2 gap-5 sm:gap-6" data-id="element-1122" data-mp="14u1fdrprinn8">
              {TEAM_VIDEOS.map(video => <VideoFrame key={video.name} video={video} data-id="element-1123" data-mp="1elntpwxed2u7" />)}
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau de repères */}
      <section className="bg-lavande py-10" data-id="element-1124" data-mp="1sjchbciumo2y">
        <div className="container max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-1125" data-mp="1nlu2x6m2bgnk">
          <div data-id="element-1126" data-mp="3uc0dj1x2sb98">
            <p className="font-heading text-3xl font-extrabold text-primary-dark" data-id="element-1127" data-mp="3udl64lztxk4g">
              3
            </p>
            <p className="text-sm font-semibold text-primary-dark/70" data-id="element-1128" data-mp="3pfa06mcnntri">
              formations diplômantes
            </p>
          </div>
          <div data-id="element-1129" data-mp="29ures4ehkk1x">
            <p className="font-heading text-3xl font-extrabold text-primary-dark" data-id="element-1130" data-mp="29vanqvm5ry3t">
              100%
            </p>
            <p className="text-sm font-semibold text-primary-dark/70" data-id="element-1131" data-mp="1v4tq7ncn9sc7">
              en alternance
            </p>
          </div>
          <div data-id="element-1132" data-mp="20350fz6uq4uy">
            <p className="font-heading text-3xl font-extrabold text-primary-dark" data-id="element-1133" data-mp="202lgkcxn56ea">
              Qualiopi
            </p>
            <p className="text-sm font-semibold text-primary-dark/70" data-id="element-1134" data-mp="1r4032w710hkd">
              processus certifié
            </p>
          </div>
          <div data-id="element-1135" data-mp="1c8alda8viqlg">
            <p className="font-heading text-3xl font-extrabold text-primary-dark" data-id="element-1136" data-mp="1c8tsrx0wdwl8">
              0 €
            </p>
            <p className="text-sm font-semibold text-primary-dark/70" data-id="element-1137" data-mp="1h86hknbbxdqg">
              de frais pour l'apprenti
            </p>
          </div>
        </div>
      </section>

      {/* Notre ADN */}
      <section className="py-24 bg-white" data-id="element-1138" data-mp="3bi0lq40074kh">
        <div className="container max-w-6xl mx-auto" data-id="element-1139" data-mp="1zb9marr3dzms">
          <SectionHeading title="Notre ADN en 4 mots" subtitle="Les principes qui guident notre façon de former et d'accompagner." centered className="mb-16" data-id="element-1140" data-mp="1zcuck56diweu" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1141" data-mp="1klv7h44qyq00">
            {VALUES.map(value => {
            const Icon = value.icon;
            return <Card key={value.title} className="border-none shadow-lg bg-white overflow-hidden hover:-translate-y-2 transition-transform duration-300 group" data-id="element-1142" data-mp="1fo3jgl783o13">
                  <div className={`h-2 w-full ${value.bar}`} data-id="element-1143" data-mp="16nx8b5yzz053" />
                  <CardContent className="p-7" data-id="element-1144" data-mp="11oasqpvp57t4">
                    <div className={`w-16 h-16 ${value.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`} data-id="element-1145" data-mp="3103h5y4f54u8">
                      <Icon className={`w-8 h-8 ${value.iconColor}`} data-id="element-1146" data-mp="33l2z9rubmn2y" />
                    </div>
                    <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-2" data-id="element-1147" data-mp="38j4fm50d48aq">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed" data-id="element-1148" data-mp="3db4j27fn5jc2">
                      {value.text}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </section>

      {/* Mot du fondateur */}
      <section className="py-24 bg-muted relative overflow-hidden" data-id="element-1149" data-mp="2pla7dt7260te">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-lavande rounded-full blur-[130px] opacity-50 pointer-events-none" data-id="element-1150" data-mp="2ujlh83xcsbbm" />
        <div className="container max-w-5xl mx-auto relative z-10" data-id="element-1151" data-mp="2zglh1x3m0hua">
          <div className="text-center mb-14" data-id="element-1152" data-mp="9t47yjesgxu8">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-4" data-id="element-1153" data-mp="esgxjbx1oj0i">
              Le mot du fondateur
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-primary-dark tracking-tight" data-id="element-1154" data-mp="1djkox7tfzqpw">
              Nous accompagnons des parcours de vie
            </h2>
          </div>

          <Card className="border-none shadow-2xl bg-white overflow-hidden" data-id="element-1155" data-mp="1meqvwa4g8eu0">
            <div className="h-2 w-full bg-brand-orange" data-id="element-1156" data-mp="1rbquy12vshbm" />
            <CardContent className="p-8 md:p-14" data-id="element-1157" data-mp="un2ps5c9kojm">
              <div className="flex flex-col md:flex-row gap-10 md:gap-14" data-id="element-1158" data-mp="uodt641srua7">
                {/* Signature en colonne */}
                <div className="md:w-56 shrink-0" data-id="element-1159" data-mp="yqh852nodh08">
                  <div className="md:sticky md:top-28" data-id="element-1160" data-mp="13mfz8y0e1d4c">
                    <div className="w-20 h-20 rounded-3xl bg-primary flex items-center justify-center text-white font-extrabold font-heading text-2xl mb-5" data-id="element-1161" data-mp="18mkigvsokly9">
                      SM
                    </div>
                    <p className="font-extrabold font-heading text-xl text-primary-dark leading-tight" data-id="element-1162" data-mp="2k1r3c9wlccj8">
                      Samir MOKEDDEM
                    </p>
                    <p className="text-brand-orange font-semibold text-sm mb-6" data-id="element-1163" data-mp="8dm4ygc6473d">
                      Fondateur & Directeur
                    </p>
                    <div className="hidden md:block" data-id="element-1164" data-mp="3utglk8exwlyu">
                      <Quote className="w-10 h-10 text-lavande" data-id="element-1165" data-mp="3urvs6m4d392k" />
                    </div>
                  </div>
                </div>

                <div className="flex-1" data-id="element-1166" data-mp="3lt0j929xnkvk">
                  <p className="text-xl md:text-2xl font-bold font-heading text-primary-dark leading-snug mb-8 pb-8 border-b border-slate-100" data-id="element-1167" data-mp="3luv8gss8lz5g">
                    Chez Zillenium Alternance, nous ne formons pas seulement des
                    apprenants : nous accompagnons des parcours de vie.
                  </p>

                  <div className="space-y-6 text-lg text-muted-foreground leading-relaxed" data-id="element-1168" data-mp="36y47if2x0fdq">
                    <p data-id="element-1169" data-mp="1a2y9bl163n0o">
                      L'alternance est une étape importante. Pour un jeune,
                      c'est souvent le premier vrai contact avec le monde
                      professionnel, ses exigences, ses codes, ses
                      responsabilités, mais aussi ses opportunités. Pour une
                      entreprise, c'est un engagement fort : accueillir, former,
                      transmettre et faire grandir un futur professionnel.
                    </p>
                    <p data-id="element-1170" data-mp="2um1r1rbgww9f">
                      Notre mission est de créer les conditions d'une
                      collaboration réussie entre trois acteurs essentiels :{' '}
                      <span className="font-semibold text-primary-dark" data-id="element-1171" data-mp="3n94p6ic8f4z5">
                        l'apprenant, l'entreprise et le CFA.
                      </span>
                    </p>
                    <p data-id="element-1172" data-mp="3db8etsr4ce23">
                      Nous accompagnons chaque jeune dans sa progression, sa
                      posture professionnelle, sa recherche d'entreprise, sa
                      confiance en lui et sa capacité à trouver sa place. Nous
                      savons que derrière chaque parcours, il peut y avoir des
                      doutes, des freins, des difficultés personnelles, sociales
                      ou psychologiques. C'est pourquoi notre accompagnement ne
                      se limite pas à la formation : il repose sur l'écoute, la
                      proximité, le suivi et la confiance.
                    </p>
                    <p data-id="element-1173" data-mp="1bn3up6ppjyjk">
                      Nous accompagnons également les entreprises dans
                      l'intégration de leurs alternants, afin de sécuriser le
                      parcours, faciliter la communication et construire une
                      relation durable. Notre rôle est d'être un lien permanent
                      entre le jeune, l'employeur et l'équipe pédagogique.
                    </p>
                    <p data-id="element-1174" data-mp="svt7azj6y3wf">
                      Chez Zillenium Alternance, nous sommes sur le terrain,
                      proches des jeunes, des familles, des entreprises et des
                      partenaires locaux. Nous comprenons les réalités de notre
                      territoire, les enjeux de l'insertion professionnelle et
                      les besoins d'accompagnement humain.
                    </p>
                  </div>

                  <p className="mt-8 bg-lavande rounded-2xl p-6 font-bold text-primary-dark text-lg leading-relaxed" data-id="element-1175" data-mp="5hy254dyxcp">
                    Rejoindre Zillenium Alternance, c'est intégrer un CFA qui
                    forme, accompagne et reste présent à chaque étape du
                    parcours.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Vie étudiante */}
      <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1176" data-mp="nwnd4i2i5lly">
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[110px] opacity-30 pointer-events-none" data-id="element-1177" data-mp="2kp72781pnqeo" />
        <div className="container max-w-5xl mx-auto relative z-10" data-id="element-1178" data-mp="2i495ryesqic2">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-white/5 p-8 md:p-12 rounded-[2rem] border border-white/10" data-id="element-1179" data-mp="2i5k9xzc5ls3p">
            <div className="text-center md:text-left" data-id="element-1180" data-mp="28dwn6bqjkz8k">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3" data-id="element-1181" data-mp="28cd7am8ssq7b">
                Vie étudiante
              </span>
              <h2 className="font-extrabold font-heading text-3xl mb-4" data-id="element-1182" data-mp="311sgr7kteb14">
                L'ambiance Zillenium, en vidéo
              </h2>
              <p className="text-lavande text-lg max-w-md" data-id="element-1183" data-mp="2w27hew1qg0nv">
                Projets solidaires, événements fédérateurs et une vraie vie de
                promo : découvrez le BDE Zillenium et ses reportages.
              </p>
            </div>
            <Button size="lg" className="bg-white hover:bg-lavande text-primary-dark text-lg px-8 py-7 shrink-0 group" asChild data-id="element-1184" data-mp="1fq0rnacplddd">
              <Link to="/bde" className="flex items-center" data-id="element-1185" data-mp="1d4t1c21mpdzs">
                Découvrir le BDE
                <PlayCircle className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" data-id="element-1186" data-mp="1i45m8h8xwazq" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Qualiopi */}
      <section className="py-24 bg-lavande relative overflow-hidden" data-id="element-1187" data-mp="185q6fafd4o5j">
        <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-white rounded-full blur-[120px] opacity-60 pointer-events-none" data-id="element-1188" data-mp="1882nzmh7kwi2" />
        <div className="container max-w-6xl relative z-10" data-id="element-1189" data-mp="z74j71yy4kyf">
          <QualiopiTeaser data-id="element-1190" data-mp="khgdzpnn925f" />
        </div>
      </section>

      {/* Contact direct */}
      <section className="py-24 bg-white" data-id="element-1191" data-mp="pfi6t2avgfu3">
        <div className="container max-w-6xl mx-auto" data-id="element-1192" data-mp="pg9s9kar8x6n">
          <ContactCTA title="Envie d'en savoir plus sur notre CFA ?" subtitle="Visite, portes ouvertes, questions sur l'accompagnement : appelez-nous, nous prenons le temps d'échanger avec vous." action={{
          label: 'Voir nos formations',
          to: '/formations'
        }} data-id="element-1193" data-mp="bg8pfzaxjfoj" />
        </div>
      </section>
    </div>;
}